import { useState } from "react";
import { AppDialog } from "../../components/AppDialog";
import type { CrudChoiceField, CrudListAction } from "./moduleCrud";

/** Local YYYY-MM-DD for a date input's default (local parts, no UTC shift — ADR-0008). */
function todayLocalIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function initialExtras(fields: CrudChoiceField[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    out[f.key] = f.type === "currency" ? "0.00" : f.type === "date" && f.defaultToday ? todayLocalIso() : "";
  }
  return out;
}

/**
 * The pick-a-value dialog for a {@link CrudListAction} that declares `choices` — e.g.
 * "Close Prospects" → Won / Cancelled, plus any `extraFields` (Actual Revenue + Close Date,
 * legacy CloseCRMProspects popup). The chosen value + the extra-field values are handed back
 * through `onConfirm`, which the list runs through its shared POST + success + refresh path.
 * A plain Yes/No `listAction` never opens this; only one with `choices` does.
 *
 * Laid out to the AI Standard Form Layout (standing rule 12): label-beside-field via the shared
 * `.sd-grid--side` / `.sd-row` / `.sd-label` / `.sd-input` classes, so the controls are 26px tall
 * with the yellow editable cue and auto ":" labels — identical to the Settings detail forms.
 */
export function ListActionChoiceDialog({ action, onConfirm, onClose }: {
  action: CrudListAction;
  onConfirm: (value: string, extras: Record<string, string>) => void | Promise<void>;
  onClose: () => void;
}) {
  const choices = action.choices ?? [];
  const extraFields = action.extraFields ?? [];
  const [value, setValue] = useState(choices[0]?.value ?? "");
  const [extras, setExtras] = useState<Record<string, string>>(() => initialExtras(extraFields));
  const [busy, setBusy] = useState(false);
  const setExtra = (key: string, v: string) => setExtras((prev) => ({ ...prev, [key]: v }));
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(value, extras); } finally { setBusy(false); }
  };
  const choiceLabel = action.choiceLabel ?? "Status";
  return (
    <AppDialog
      open
      title={action.confirmTitle ?? action.label}
      width={420}
      className="dlg-center"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={busy} onClick={() => void confirm()}>{action.label}</button>
          <button type="button" className="ca-btn" disabled={busy} onClick={onClose}>Cancel</button>
        </>
      }
    >
      {/* AI Standard Form Layout — single stacked column of side-label rows (reuses the shared
          Settings-detail classes so the control size / ":" labels match). `sd-grid sd-grid--plain`
          adds the owner's PLAIN-controls standard: WHITE boxes (no yellow) + SQUARE corners, size
          unchanged (owner report 2026-10-28, image 1044). The inline display:flex overrides
          .sd-grid's grid display, so only the input background/corners change, not the 1-column layout. */}
      <div className="sd-grid sd-grid--side sd-grid--plain" style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 4px" }}>
        <div className="sd-row">
          <label className="sd-label">{choiceLabel}</label>
          <select className="sd-input" value={value} onChange={(e) => setValue(e.target.value)}>
            {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        {extraFields.map((f) => (
          <div className="sd-row" key={f.key}>
            <label className="sd-label">{f.label}</label>
            {f.type === "currency" ? (
              <div style={{ position: "relative", width: "100%" }}>
                <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--ai-muted)", fontSize: 13, pointerEvents: "none" }}>$</span>
                <input
                  type="number"
                  step="0.01"
                  className="sd-input"
                  style={{ width: "100%", paddingLeft: 18 }}
                  value={extras[f.key] ?? ""}
                  onChange={(e) => setExtra(f.key, e.target.value)}
                />
              </div>
            ) : (
              <input
                type="date"
                className="sd-input"
                style={{ width: "100%" }}
                value={extras[f.key] ?? ""}
                onChange={(e) => setExtra(f.key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>
    </AppDialog>
  );
}
